import { useEffect, useMemo, useState, type MouseEvent } from 'react';
import { FeatureGate } from '../../components/FeatureGate';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Loader2, Cable, Wand2, Sparkles, Webhook, ArrowRight, CheckCircle2, Clock, PauseCircle, PlayCircle, AlertTriangle } from 'lucide-react';
import { useStore } from '../../store/StoreContext';
import { SectionTitle } from '../../components/ui/SectionTitle';
import { AppCard } from '../../components/ui/AppCard';
import { IconButton } from '../../components/ui/IconButton';
import { Button } from '../../components/ui/Button';
import { TextArea } from '../../components/ui/TextField';
import { SyncButton } from '../../components/ui/SyncButton';
import { EmptyState } from '../../components/ui/EmptyState';
import { useInfiniteScroll } from '../../lib/useInfiniteScroll';
import { deriveIntegrationView, integrationStateCopy, type IntegrationLifecycleState } from '../../lib/integrationView';
import { IntegrationErrorPanel } from '../../components/integrations/IntegrationBoundary';
import type { Mapping } from '../../types';
import { AiProviderPicker } from '../../components/ai/AiProviderPicker';
import { ChannelSelect, SchemaSelect } from '../../components/integrations/DestinationSelects';
import { useOwnedTemplates } from '../../lib/useOwnedTemplates';

// Icon-plus-text per state (brief: "not by color alone" / "every health
// state includes text and an icon"). Colors are a hint, not the signal.
const stateIcon: Record<IntegrationLifecycleState, typeof CheckCircle2> = {
  configured: CheckCircle2,
  waiting_first_event: Clock,
  paused: PauseCircle,
  configuration_incomplete: AlertTriangle,
};

const stateColor: Record<IntegrationLifecycleState, string> = {
  configured: 'text-emerald-600 dark:text-emerald-400',
  waiting_first_event: 'text-amber-600 dark:text-amber-400',
  paused: 'text-black/40 dark:text-white/40',
  configuration_incomplete: 'text-red-600 dark:text-red-400',
};

export function StatePill({ state }: { state: IntegrationLifecycleState }) {
  const Icon = stateIcon[state];
  const copy = integrationStateCopy[state];
  return (
    <span
      className={`inline-flex items-center gap-1 shrink-0 rounded-full border border-black/10 dark:border-white/15 px-2 py-0.5 text-[11px] font-medium ${stateColor[state]}`}
      title={copy.description}
    >
      <Icon size={12} aria-hidden />
      {copy.label}
    </span>
  );
}

function IntegrationCard({ mapping }: { mapping: Mapping }) {
  const { setMappingEnabled, isMappingOwner } = useStore();
  const view = useMemo(() => deriveIntegrationView(mapping), [mapping]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const paused = mapping.enabled === false;

  const toggle = async (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setBusy(true);
    setError('');
    try {
      await setMappingEnabled(mapping.nickname, paused);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to update this integration.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Link to={`/integrations/${mapping.id}`}>
      <AppCard className="flex flex-col gap-1.5">
        <div className="flex items-center gap-2 min-w-0">
          <span className="font-semibold truncate">{mapping.title || mapping.nickname}</span>
          {mapping.isPublic && (
            <span className="shrink-0 rounded-full bg-blue-600/15 text-blue-600 dark:text-blue-400 text-[11px] font-medium px-2 py-0.5">
              Public
            </span>
          )}
          {mapping.sourceType && (
            <span className="shrink-0 rounded-md bg-black/[0.06] dark:bg-white/10 text-black/60 dark:text-white/60 text-[11px] font-mono px-1.5 py-0.5">
              {mapping.sourceType}
            </span>
          )}
          <div className="flex-1" />
          <StatePill state={view.state} />
          {isMappingOwner(mapping) && (
            <IconButton
              icon={busy ? Loader2 : paused ? PlayCircle : PauseCircle}
              onClick={(e) => void toggle(e)}
              disabled={busy}
              className={busy ? 'animate-spin' : undefined}
              aria-label={paused ? `Resume ${mapping.title || mapping.nickname}` : `Pause ${mapping.title || mapping.nickname}`}
              title={paused ? 'Resume this integration' : 'Pause this integration'}
            />
          )}
        </div>
        <span className="text-sm text-black/50 dark:text-white/50 font-mono truncate">
          {view.destinationChannel ? `→ #${view.destinationChannel}` : mapping.nickname}
        </span>
        {mapping.description && (
          <span className="text-xs text-black/40 dark:text-white/40 line-clamp-2">{mapping.description}</span>
        )}
        {error && <span className="text-xs text-red-500">{error}</span>}
      </AppCard>
    </Link>
  );
}

// Default Integrations screen (roadmap #9 Phase 1). Built entirely from
// existing Mapping data - no new backend route, no Activity tab, no
// fabricated health/timestamps (see src/lib/integrationView.ts). The
// connector catalog / GitHub fast path / visual Mapping editor are
// deliberately out of scope for this slice - see docs/CLAUDE_ZMIST_ROADMAP.md
// #9 for what's still gated on owner decisions D3/D4.
export function IntegrationsListScreen() {
  const { mappings, syncMappings, loadMoreMappings, mappingsHasMore, manualRefresh, channels, isChannelOwner } = useStore();
  const ownedChannels = channels.filter(isChannelOwner);
  const { templates: ownedTemplates } = useOwnedTemplates();
  const [pickChannel, setPickChannel] = useState('');
  const [pickSchema, setPickSchema] = useState('');
  const [query, setQuery] = useState('');
  const [syncError, setSyncError] = useState('');
  const [aiText, setAiText] = useState('');
  const navigate = useNavigate();
  // Go hands the description to the AI integration screen, which starts
  // mapping straight away (nothing is created until the user confirms).
  const goAi = () => {
    if (!aiText.trim()) return;
    navigate('/integrations/new/ai', { state: { text: aiText, run: true, pickChannel, pickSchema } });
  };
  // No AI: the AI screen creates the webhook + Bearer token straight away.
  const goWebhook = () => navigate('/integrations/new/ai', { state: { text: aiText, webhook: true, pickChannel, pickSchema } });

  const runSync = () => {
    setSyncError('');
    return syncMappings().catch((e) => {
      setSyncError(e instanceof Error ? e.message : 'Unable to load sources.');
    });
  };

  useEffect(() => {
    if (manualRefresh) return;
    void runSync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sentinelRef = useInfiniteScroll(loadMoreMappings, mappingsHasMore);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return mappings;
    return mappings?.filter(
      (m) =>
        m.title.toLowerCase().includes(q) ||
        m.nickname.toLowerCase().includes(q) ||
        m.sourceType.toLowerCase().includes(q)
    );
  }, [mappings, query]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <SectionTitle title="Sources" subtitle="Connected sources that turn external data into signals" />
        <div className="flex-1" />
        {manualRefresh && <SyncButton onSync={syncMappings} ariaLabel="Sync sources" />}
        <Button variant="bordered" className="!px-3 !py-1.5 !text-[13px]" onClick={() => navigate('/integrations/agents')}>
          Agents
        </Button>
        <FeatureGate feature="integrations.ai_mapping" fallback={null}>
          <Button variant="bordered" className="!px-3 !py-1.5 !text-[13px]" onClick={() => navigate('/integrations/new/auto')}>
            <Wand2 size={14} aria-hidden /> Wizard
          </Button>
        </FeatureGate>
        <Link to="/integrations/catalog">
          <IconButton icon={Plus} variant="filled" aria-label="Add source" />
        </Link>
      </div>

      <FeatureGate feature="integrations.ai_mapping" fallback={null}>
      <div className="flex flex-col gap-2">
        <TextArea
          aria-label="Describe a source for the AI"
          value={aiText}
          onChange={(e) => setAiText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) goAi();
          }}
          rows={2}
          placeholder={'Ask the AI to add a source - e.g. Track all metrics from https://… for Apple'}
        />
        <div className="grid gap-2 sm:grid-cols-2">
          <ChannelSelect channels={ownedChannels} value={pickChannel} onChange={setPickChannel} />
          <SchemaSelect templates={ownedTemplates} value={pickSchema} onChange={setPickSchema} />
        </div>
        <AiProviderPicker />
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={goWebhook}>
            <Webhook size={16} aria-hidden /> Webhook + token
          </Button>
          <Button onClick={goAi} disabled={!aiText.trim()}>
            <Sparkles size={16} aria-hidden /> Go <ArrowRight size={16} aria-hidden />
          </Button>
        </div>
      </div>
      </FeatureGate>

      {syncError && <IntegrationErrorPanel title="Could not load sources" message={syncError} onRetry={() => void runSync()} />}

      {mappings?.length === 0 ? (
        <div className="flex flex-col items-center gap-4">
          <EmptyState
            icon={Cable}
            title="No sources yet"
            subtitle="Describe one above, or connect GitHub, a webhook, Prometheus, or your own agent to start creating signals automatically."
          />
          <div className="flex flex-wrap justify-center gap-2">
            <Button onClick={() => navigate('/integrations/catalog')}>Browse connectors</Button>
            <FeatureGate feature="integrations.ai_mapping" fallback={null}>
              <Button variant="bordered" onClick={() => navigate('/integrations/new/auto')}>
                <Wand2 size={16} aria-hidden /> Wizard
              </Button>
            </FeatureGate>
          </div>
        </div>
      ) : (
        <>
          {mappings?.length > 4 && (
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filter sources"
              className="rounded-xl border border-black/15 dark:border-white/15 bg-white dark:bg-white/5 px-3.5 py-2.5 text-[15px] outline-none focus:border-blue-500"
            />
          )}
          <div className="flex flex-col gap-3">
            {filtered?.map((m) => (
              <IntegrationCard key={m.id} mapping={m} />
            ))}
          </div>
        </>
      )}

      {mappings?.length > 0 && mappingsHasMore && (
        <div ref={sentinelRef} className="flex justify-center py-4">
          <Loader2 size={20} className="animate-spin text-black/30 dark:text-white/30" />
        </div>
      )}
    </div>
  );
}
